import { useInsertionEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { activeCapabilities } from '../core/input/activeAdapter';
import { activate, registerActivation } from '../core/input/activationRegistry';
import { HOLD_MS, useHoldGesture } from './useHoldGesture';

/**
 * Every interactive target in the app.
 *
 * It does two things a plain `<button onClick>` cannot:
 *
 *  1. registers what its id *means* in the activation registry, so the input adapters — direct
 *     touch now, dwell and scanning in Phase 3 — can activate it without any surface knowing they
 *     exist (§5.1);
 *  2. routes its own DOM click through the same registry, so a hardware switch sending Space or
 *     Enter (§2.5) and a real tap end up in exactly one code path — except in a timed mode, where the
 *     adapter owns pointers and the click must be ignored. See the handler.
 *
 * Disabled targets drop `data-activate-id` entirely, so the adapter does not record a miss against
 * something that was never going to respond — a disabled button is not a failed aim.
 *
 * ── `hold`, and why it is a prop here rather than a hook at the call site ──
 *
 * One target may carry a second gesture (`SUR-26`). The pointer handlers have to sit on the element that
 * carries `data-activate-id`: a wrapper around this button would capture the pointer, and capture retargets
 * later events so the adapter's `closest('[data-activate-id]')` finds nothing and the ordinary tap breaks.
 * That element is created here, so the prop is here.
 *
 * It is **not** an `Intent`. A hold intent would make five adapters answer a question four of them cannot,
 * and `activeCapabilities().isTimed` already answers it: in dwell a hold *is* activation and scanning sends
 * no pointer at all, so the gesture is simply not attached in a timed mode.
 */
export function ActivateButton({
  id,
  onActivate,
  children,
  disabled = false,
  style,
  label,
  pressed,
  testId,
  hold,
}: {
  id: string;
  onActivate: () => void;
  children: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  /** Accessible name, where the visible content is a glyph or a number. */
  label?: string;
  /** For toggles: reflected as aria-pressed. */
  pressed?: boolean;
  testId?: string;
  /**
   * A second gesture on the same target. Ignored in a timed input mode, and the caller is responsible for
   * suppressing the activation the release would otherwise produce — see `suppressActivations`.
   */
  hold?: { ms?: number; onHold: () => void };
}): React.JSX.Element {
  const handlerRef = useRef(onActivate);
  handlerRef.current = onActivate;

  // Called unconditionally, because a hook cannot be conditional — the gesture is attached or not by
  // whether its handlers are spread below.
  const holdHandlers = useHoldGesture(() => hold?.onHold(), hold?.ms ?? HOLD_MS);
  const holdAttached = hold !== undefined && !disabled && !activeCapabilities().isTimed;

  // Insertion effect so registration happens before the browser can deliver a pointerup for a
  // freshly rendered target — the person may already be mid-tap when a surface changes.
  useInsertionEffect(() => {
    if (disabled) return;
    return registerActivation(id, () => handlerRef.current());
  }, [id, disabled]);

  return (
    <button
      type="button"
      {...(disabled ? {} : { 'data-activate-id': id })}
      {...(testId ? { 'data-testid': testId } : { 'data-testid': id })}
      {...(label ? { 'aria-label': label } : {})}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      {...(holdAttached ? holdHandlers : {})}
      disabled={disabled}
      onClick={(event) => {
        // ── Why this is not simply `activate(id)` ──
        //
        // A pointer-derived click has to be ignored in a timed input mode, and the flow suite found out
        // why the hard way: in dwell mode every tap activated *twice* — once when the dwell completed and
        // again from the click the browser synthesises on release. Keys doubled, navigation toggled
        // straight back, and a quick tap selected without any holding at all, which is precisely the
        // accidental activation 3.1's acceptance forbids.
        //
        // `event.detail` is the discriminator: greater than zero means a real pointer click, which in a
        // timed mode belongs to the adapter and to nobody else. Zero means the click was synthesised
        // without a pointer — a keyboard Space or Enter, which is how a Bluetooth switch presents itself
        // (§2.5) — and that path must keep working in every mode.
        if (event.detail > 0 && activeCapabilities().isTimed) return;
        activate(id);
      }}
      style={{ transition: 'none', ...style }}
    >
      {children}
    </button>
  );
}
